import { useEffect, useMemo, useRef } from 'react';
import { useCurrentTheme } from '../theme';

/**
 * Seizoensthema: subtiele deeltjes over Home, afhankelijk van de datum.
 * Sneeuw (december), vuurwerk (oud & nieuw), Halloween (eind oktober), Koningsdag (26–27 april).
 * Klikt nergens tussen (pointer-events: none) en staat uit bij "minder beweging".
 */

type Season = 'snow' | 'fireworks' | 'halloween' | 'kingsday';

export function seasonFor(d: Date): Season | null {
  const m = d.getMonth() + 1;
  const day = d.getDate();
  if ((m === 12 && day === 31) || (m === 1 && day === 1)) return 'fireworks';
  if (m === 12) return 'snow';
  if (m === 10 && day >= 25) return 'halloween';
  if (m === 4 && (day === 26 || day === 27)) return 'kingsday';
  return null;
}

interface P { x: number; y: number; vx: number; vy: number; r: number; life: number; color: string; glyph?: string }

export function SeasonalLayer() {
  const theme = useCurrentTheme();
  const canvas = useRef<HTMLCanvasElement>(null);
  const season = useMemo(() => seasonFor(new Date()), []);
  const off = theme?.components?.seasonal === 'off';

  useEffect(() => {
    const c = canvas.current;
    if (!c || !season || off) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    let w = 0;
    let h = 0;
    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      w = window.innerWidth;
      h = window.innerHeight;
      c.width = w * dpr;
      c.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const parts: P[] = [];
    const rnd = (a: number, b: number) => a + Math.random() * (b - a);
    const spawnFalling = (glyphs: string[] | null, colors: string[]) => {
      parts.push({
        x: rnd(0, w), y: -20, vx: rnd(-0.3, 0.3), vy: rnd(0.4, 1.2), r: rnd(1.2, 3.2), life: Infinity,
        color: colors[Math.floor(Math.random() * colors.length)],
        glyph: glyphs ? glyphs[Math.floor(Math.random() * glyphs.length)] : undefined,
      });
    };
    const burst = () => {
      const x = rnd(w * 0.15, w * 0.85);
      const y = rnd(h * 0.12, h * 0.45);
      const hue = Math.floor(rnd(0, 360));
      for (let i = 0; i < 46; i++) {
        const a = (i / 46) * Math.PI * 2;
        const s = rnd(1.2, 3.2);
        parts.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, r: 1.8, life: rnd(50, 80), color: `hsl(${hue + rnd(-20, 20)} 95% 65%)` });
      }
    };

    const max = season === 'snow' ? 70 : season === 'fireworks' ? 600 : 22;
    let frame = 0;
    let raf = 0;
    let visible = !document.hidden;
    const onVis = () => {
      visible = !document.hidden;
      if (visible) raf = requestAnimationFrame(tick);
    };
    document.addEventListener('visibilitychange', onVis);

    function tick() {
      if (!visible || !ctx) return;
      frame++;
      ctx.clearRect(0, 0, w, h);
      if (season === 'snow' && parts.length < max && frame % 6 === 0) spawnFalling(null, ['rgba(255,255,255,0.85)']);
      if (season === 'halloween' && parts.length < max && frame % 40 === 0) spawnFalling(['🎃', '🦇', '👻'], ['#fff']);
      if (season === 'kingsday' && parts.length < max && frame % 30 === 0) spawnFalling(['🧡', '👑', '🎈'], ['#ff7a00']);
      if (season === 'fireworks' && frame % 70 === 0 && parts.length < max) burst();

      for (let i = parts.length - 1; i >= 0; i--) {
        const p = parts[i];
        if (season === 'fireworks') {
          p.vy += 0.03;
          p.vx *= 0.985;
          p.vy *= 0.985;
          p.life -= 1;
        } else {
          p.vx += Math.sin((frame + i * 13) / 60) * 0.01;
        }
        p.x += p.vx;
        p.y += p.vy;
        if (p.life <= 0 || p.y > h + 30) {
          parts.splice(i, 1);
          continue;
        }
        ctx.globalAlpha = season === 'fireworks' ? Math.max(0, Math.min(1, p.life / 40)) : 0.75;
        if (p.glyph) {
          ctx.font = '22px system-ui, sans-serif';
          ctx.fillText(p.glyph, p.x, p.y);
        } else {
          ctx.fillStyle = p.color;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [season, off]);

  if (!season || off) return null;
  return <canvas ref={canvas} className="seasonal-layer" aria-hidden="true" />;
}
